import React, { useState, useEffect } from 'react';
import { X, Settings, Trash2, UserPlus, Clock } from 'lucide-react';
import { apiGet, apiPatch, apiPost, apiDelete } from '../../utils/api';
import UserSearchInput from './UserSearchInput';

const EditLeaderboardModal = ({ leaderboard, onClose, onUpdate }) => {
  const [name, setName] = useState(leaderboard.name);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState(leaderboard.metrics || []);
  const [members, setMembers] = useState(leaderboard.members || []);
  const [pendingInvitations, setPendingInvitations] = useState(leaderboard.pending_invitations || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await apiGet('/leaderboards/available_metrics');
      if (response.ok) {
        const data = await response.json();
        setAvailableMetrics(data.metrics || []);
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
    }
  };

  const toggleMetric = (key) => {
    setSelectedMetrics(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleInviteUser = async (user) => {
    try {
      const response = await apiPost(`/leaderboards/${leaderboard.id}/invite`, {
        user_public_id: user.public_id
      });
      if (response.ok) {
        const data = await response.json();
        setPendingInvitations(prev => [...prev, {
          id: data.invitation.id,
          public_id: user.public_id,
          username: user.username,
          full_name: user.full_name,
          status: 'pending'
        }]);
        setError('');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to invite user');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveMember = async (publicId) => {
    if (publicId === leaderboard.creator_public_id) return;
    try {
      const response = await apiDelete(`/leaderboards/${leaderboard.id}/members/${publicId}`);
      if (response.ok) {
        setMembers(prev => prev.filter(m => m.public_id !== publicId));
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (selectedMetrics.length === 0) {
      setError('Select at least one metric');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await apiPatch(`/leaderboards/${leaderboard.id}`, {
        name: name.trim(),
        metric_keys: selectedMetrics
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update');
      }
      onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const allExcludedIds = [
    ...members.map(m => m.public_id),
    ...pendingInvitations.map(p => p.public_id)
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Settings size={20} className="text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-800 font-unna">Edit Leaderboard</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-5">
          {error && (
            <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm font-rasa">{error}</div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-rasa">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-rasa focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Metrics */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 font-rasa">Metrics</label>
            <div className="grid grid-cols-2 gap-2">
              {availableMetrics.map(m => (
                <button
                  key={m.key}
                  onClick={() => toggleMetric(m.key)}
                  className={`px-3 py-2 rounded-lg text-sm font-rasa text-left transition-colors ${
                    selectedMetrics.includes(m.key)
                      ? 'bg-blue-100 text-blue-700 border border-blue-300'
                      : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Members */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 font-rasa">
              <UserPlus size={14} className="inline mr-1" /> Invite Members
            </label>
            <UserSearchInput
              onSelect={handleInviteUser}
              excludeIds={allExcludedIds}
            />

            {/* Current members */}
            {members.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-gray-500 font-rasa mb-1 uppercase tracking-wider">Members</p>
                <div className="space-y-1">
                  {members.map(member => (
                    <div key={member.public_id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <div className="flex items-center gap-2">
                        {member.avatar_url ? (
                          <img src={member.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold flex-shrink-0">
                            {(member.full_name || member.username || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div>
                          <span className="text-sm text-gray-700 font-rasa">
                            {member.full_name || member.username}
                            {member.public_id === leaderboard.creator_public_id && (
                              <span className="ml-1 text-xs text-blue-500">(creator)</span>
                            )}
                          </span>
                          {member.email && <p className="text-xs text-gray-400 font-rasa">{member.email}</p>}
                        </div>
                      </div>
                      {member.public_id !== leaderboard.creator_public_id && (
                        <button onClick={() => handleRemoveMember(member.public_id)} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending invitations */}
            {pendingInvitations.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-gray-500 font-rasa mb-1 uppercase tracking-wider">Pending Invitations</p>
                <div className="space-y-1">
                  {pendingInvitations.map(inv => (
                    <div key={inv.public_id} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-blue-400" />
                        <span className="text-sm text-blue-700 font-rasa">{inv.full_name || inv.username}</span>
                      </div>
                      <span className="text-xs text-blue-500 font-rasa">Pending</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-rasa">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 disabled:opacity-50 font-rasa"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditLeaderboardModal;
